import React, { useState } from 'react';
import {
  Box, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Typography,
  CircularProgress, Alert,
} from '@mui/material';
import { PlayArrow as RunIcon } from '@mui/icons-material';
import { executeQuery } from '../../api/connections';

const QueryEditor = ({ connectionId, initialQuery = '' }) => {
  const [sql, setSql] = useState(initialQuery);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (initialQuery) setSql(initialQuery);
  }, [initialQuery]);

  const handleRun = async () => {
    if (!sql.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await executeQuery(connectionId, sql);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Query failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={4}
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="SELECT * FROM table_name LIMIT 100"
        variant="outlined"
        sx={{ fontFamily: 'monospace', mb: 1 }}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RunIcon />}
          onClick={handleRun}
          disabled={loading || !sql.trim()}
        >
          Run
        </Button>
        <Typography variant="caption" color="text.secondary">
          Ctrl+Enter to run
        </Typography>
        {result && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {result.row_count} rows in {result.execution_time_ms}ms
          </Typography>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result && result.rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {result.columns.map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.rows.map((row, i) => (
                <TableRow key={i} hover>
                  {result.columns.map((col) => (
                    <TableCell key={col} sx={{ whiteSpace: 'nowrap', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row[col] != null ? String(row[col]) : <em style={{ opacity: 0.4 }}>NULL</em>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {result && result.row_count === 500 && (
        <Alert severity="info" sx={{ mt: 1 }}>Showing first 500 rows. Refine your query for more specific results.</Alert>
      )}
    </Box>
  );
};

export default QueryEditor;
